<script setup lang="ts">
import { storeToRefs } from 'pinia';
import moment from 'moment';
import SettingsSection from '@/dashboard/vue/components/settings/SettingsSection.vue';
import Divider from '@/dashboard/vue/components/common/Divider.vue';
import BillingHistoryIcon from '@/dashboard/vue/components/icons/BillingHistoryIcon.vue';
import CreditCardIcon from '@/dashboard/vue/components/icons/CreditCardIcon.vue';
import { useSettingsStore } from '@/dashboard/store/settingsStore';
import { Workspace } from '@/dashboard/types/workspace';
import { WithManagementSubscription } from '@/dashboard/types/subscription';
import CloseCircleFilledIcon from '@/dashboard/vue/components/icons/CloseCircleFilledIcon.vue';
import { capitalize } from '@/dashboard/utils/capitalize';

type PropsWorkspace = Omit<Workspace, 'subscription'> & { subscription: WithManagementSubscription };

const settingsStore = useSettingsStore();
const { workspaces } = storeToRefs(settingsStore);

function getTitleStr(workspace: PropsWorkspace) {
  let base = `${workspace.name}`;

  if (workspace.subscription.scheduled_change && workspace.subscription.scheduled_change.action == 'cancel') {
    base += ` / Downgrading. Keeps until: ${moment(workspace.subscription.scheduled_change.effective_at).format('ll')}`;
  } else if (workspace.subscription.next_billed_at) {
    base += ` / Next payment: ${moment(workspace.subscription.next_billed_at).format('ll')}`;
  }

  if (workspace.subscription.status === 'active') {
    base += ` / ${(+workspace.subscription.recurring_transaction_details.totals.total / 100).toFixed(2)} ${workspace.subscription.period === 'month' ? 'monthly' : 'annually'}`;
  } else {
    base += ` / ${getWorkspaceStatus(workspace)}`;
    return base;
  }

  return base;
}

function getTeamStr(workspace: PropsWorkspace) {
  const admins = workspace.users.filter((item) => item.role === 'admin').length;
  const creators = workspace.users.filter((item) => item.role === 'creator').length;
  const owner = '1 owner';

  const array = [owner];

  if (admins > 0) {
    if (admins === 1) {
      array.push('1 admin');
    } else {
      array.push(`${admins} admins`);
    }
  }

  if (creators > 0) {
    if (creators === 1) {
      array.push('1 creator');
    } else {
      array.push(`${creators} creator`);
    }
  }

  return array.join(', ');
}

function getWorkspaceStatus(workspace: PropsWorkspace) {
  if (workspace.subscription.status === 'past_due') {
    return 'Past Due';
  } else {
    return capitalize(workspace.subscription.status);
  }
}
</script>

<template>
  <SettingsSection v-if="workspaces.length > 0">
    <div class="flex w-full flex-col">
      <h5 class="mb-m">Billing</h5>

      <Divider class="flex w-full" />

      <div v-for="workspace in workspaces" :key="workspace.id" class="flex flex-col">
        <div class="flex items-center justify-between gap-x-l">
          <div class="grid grid-cols-[24px_1fr] items-center gap-x-xs pl-xs">
            <i v-if="workspace.subscription.status === 'active'" class="m-icon-ok-round-saved" />
            <i v-else-if="workspace.subscription.status === 'past_due'" class="m-icon-not-ok-round-saved" />
            <CloseCircleFilledIcon v-else-if="workspace.subscription.status === 'paused'" class="fill-gray-700 dark:fill-mid-red" />
            <i v-else class="m-icon-note-round-saved" />

            <div class="flex flex-col py-[14px]">
              <span class="text-buttons">{{ getTitleStr(workspace) }}</span>
              <span class="text-caption-s text-gray-500">{{ getTeamStr(workspace) }}</span>
            </div>
          </div>

          <div class="flex flex-col items-center gap-xs laptop:flex-row">
            <a
              v-if="workspace.subscription.management_urls.update_payment_method"
              :href="workspace.subscription.management_urls.update_payment_method"
              target="_blank"
              class="tppr-button__animation flex items-center gap-x-xs rounded-[6px] px-xs py-xxs hover:shadow-s-hover dark:hover:bg-gray-750"
            >
              <CreditCardIcon class="stroke-black dark:stroke-white" />
              <span class="text-buttons">Settings</span>
            </a>
            <router-link
              :to="{ name: 'transactions', params: { workspace_id: workspace.id } }"
              class="tppr-button__animation flex items-center gap-x-xs rounded-[6px] px-xs py-xxs hover:shadow-s-hover dark:hover:bg-gray-750"
            >
              <BillingHistoryIcon class="stroke-black dark:stroke-white" />
              <span class="text-buttons">History</span>
            </router-link>
          </div>
        </div>
        <Divider class="flex w-full" />
      </div>
    </div>
  </SettingsSection>
</template>

<style scoped lang="scss"></style>
